import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import OnbFrame from '../_shared/OnbFrame';
import PhoneFrame from '../_shared/PhoneFrame';
import MockAppHome, { useMockAppPreview } from '../_shared/MockAppHome';
import HomeScreenWithApp from './HomeScreenWithApp';
import { COLORS } from '../_shared/tokens';
import { useOnboarding } from '../../../contexts/onboarding.context';
import { useUser } from '../../../contexts/user.context';
import useActiveTheme from '../../../hooks/activeTheme.hook';
import { mobilizeiUsersService } from '../../../services/mobilizei/users.service';
import { showApiError } from '../../../utils/errors';
import mixpanel from 'mixpanel-browser';
import { mixpanelEvents } from '../../../utils/constants';

const NEXT_STEPS = [
    {
        Icon: PaletteOutlinedIcon,
        title: 'Personalizar ainda mais',
        description: 'acesse o editor de layout completo no nosso painel'
    },
    {
        Icon: RocketLaunchOutlinedIcon,
        title: 'Publicar nas lojas de apps',
        description: 'enviamos seu aplicativo para a Apple e a Google por você'
    }
];

/**
 * D05 · Conclusão (step 7 of 7) — the aha already happened; this closes the
 * journey and converts (publishing requires a paid plan), while still offering
 * an exit for the undecided.
 */
export default function Conclude() {
    const navigate = useNavigate();
    const { data, clearOnboardingContext } = useOnboarding();
    const { user, setUser } = useUser();
    const { homeScreenSections, themeSettings } = useActiveTheme();
    const { isHidden: isMockHidden } = useMockAppPreview();

    useEffect(() => {
        function completeOnboarding() {
            mobilizeiUsersService.completeOnboarding()
        }
        completeOnboarding()
    }, [])

    // The store was already created on the Connect step, so this only marks the
    // onboarding as completed — which is what releases the panel for this user.
    const finishOnboarding = (path) => {
        const mixPanelEvent = path === '/pricing' ? mixpanelEvents.ONBOARDING_CONCLUSION_GO_TO_PLANS_CLICKED : mixpanelEvents.ONBOARDING_CONCLUSION_GO_TO_PANEL_CLICKED
        mixpanel.track(mixPanelEvent, {
            'platform_id': user.store.platformId,
            'store_id': user.store.id
        })
        setUser({ ...user, isOnboardingCompleted: true });
        clearOnboardingContext();
        navigate(path);
    }

    return (
        <OnbFrame step={7}>
            <Box
                sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1.08fr 1fr' },
                    gap: { xs: 4, md: 7 },
                    alignItems: 'center'
                }}
            >
                <Box sx={{ maxWidth: 560 }}>
                    <Typography
                        component="h1"
                        sx={{ fontWeight: 700, fontSize: { xs: 30, md: 44 }, lineHeight: 1.08, letterSpacing: '-0.8px', mb: 1.75 }}
                    >
                        Seu app está pronto para{' '}
                        <Box component="span" sx={{ color: COLORS.green }}>ser publicado!</Box>
                    </Typography>

                    <Typography sx={{ fontSize: { xs: 16, md: 18 }, lineHeight: 1.55, color: COLORS.textMuted, mb: 3.75, maxWidth: 490 }}>
                        Você já testou o app da {data.storeName} e enviou sua primeira notificação. Agora, é só publicar o app e usar as notificações sempre que quiser falar com seus clientes.
                    </Typography>

                    {/* Mobile: the phone cannot sit in a side column, so it becomes a
                        cropped band showing just the installed icon. */}
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'none' },
                            position: 'relative',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mb: 3,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            backgroundColor: COLORS.surfaceMuted,
                            pt: 2.5
                        }}
                    >
                        <Box sx={{ height: 200, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ position: 'relative' }}>
                                <HomeScreenWithApp scale={0.9} storeName={data.storeName} logoUrl={data.logoUrl} />
                            </Box>
                        </Box>

                        {/* Fade hinting the phone continues below the crop */}
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                bottom: 44,
                                height: 70,
                                pointerEvents: 'none',
                                background: `linear-gradient(to bottom, rgba(245,245,245,0), ${COLORS.surfaceMuted})`
                            }}
                        />

                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 2,
                                mt: -1,
                                mb: 2,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: '#fff',
                                borderRadius: '999px',
                                px: 1.875,
                                py: 1.125,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.14)'
                            }}
                        >
                            <SmartphoneOutlinedIcon sx={{ fontSize: 18, color: COLORS.green }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>seu app, instalado</Typography>
                        </Box>
                    </Box>

                    <Typography
                        sx={{ fontWeight: 700, fontSize: 13, letterSpacing: '0.6px', textTransform: 'uppercase', color: COLORS.textMuted, mb: 1.5 }}
                    >
                        Próximos passos
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
                        {NEXT_STEPS.map(({ Icon, title, description }) => (
                            <Box
                                key={title}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.75,
                                    p: '14px 16px',
                                    backgroundColor: '#fff',
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: '10px'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10px',
                                        flex: 'none',
                                        backgroundColor: 'rgba(60,213,104,0.12)',
                                        color: COLORS.forest,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Icon sx={{ fontSize: 22 }} />
                                </Box>

                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{title}</Typography>
                                    <Typography sx={{ fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted, mt: 0.375 }}>
                                        {description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mb: 3.5 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: COLORS.textMuted, mt: '1px', flex: 'none' }} />
                        <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: COLORS.textMuted }}>
                            Até aqui, você usou o Visualizador para testar seu app. Depois de publicado, você vai ter um app só seu,
                            com a sua marca, que seus clientes baixam na App Store e na Google Play.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: 'center',
                            gap: { xs: 2, md: 2.75 }
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => finishOnboarding('/pricing')}
                            endIcon={<ArrowForwardIcon />}
                            sx={{ width: { xs: '100%', md: 'auto' }, py: 2, fontSize: 16, boxShadow: '0 10px 28px rgba(60,213,104,0.4)' }}
                        >
                            ver planos e publicar
                        </Button>

                        <Link
                            component="button"
                            onClick={() => finishOnboarding('/')}
                            sx={{ fontSize: 15, color: COLORS.textMuted, textDecoration: 'underline', textUnderlineOffset: 3 }}
                        >
                            explorar o painel antes
                        </Link>
                    </Box>
                </Box>

                {/* Right — the installed app on the home screen, with the app running behind */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                    <Box
                        aria-hidden
                        sx={{
                            position: 'absolute',
                            width: 480,
                            height: 480,
                            background: 'radial-gradient(circle, rgba(60,213,104,0.22), transparent 70%)',
                            borderRadius: '50%'
                        }}
                    />

                    {/* The app running, tilted behind the springboard — wearing the
                        theme the merchant just personalized, not a generic mock */}
                    {!isMockHidden && (
                        <PhoneFrame scale={0.74} tilt={-6} sx={{ position: 'absolute', left: 8, zIndex: 1 }}>
                            <MockAppHome
                                homeScreenSections={homeScreenSections}
                                primaryColor={data.primaryColor || '#000'}
                                storeName={data.storeName}
                                themeSettings={themeSettings}
                            />
                        </PhoneFrame>
                    )}

                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <HomeScreenWithApp scale={0.9} storeName={data.storeName} logoUrl={data.logoUrl} />
                    </Box>

                    <Box
                        sx={{
                            position: 'absolute',
                            top: 40,
                            right: -18,
                            zIndex: 3,
                            backgroundColor: '#fff',
                            borderRadius: '999px',
                            px: 1.875,
                            py: 1.125,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                            transform: 'rotate(3deg)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <SmartphoneOutlinedIcon sx={{ fontSize: 18, color: COLORS.green }} />
                        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>seu app, instalado</Typography>
                    </Box>
                </Box>
            </Box>
        </OnbFrame>
    );
}
